import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSession } from "../session/SessionContext";
import { usePreferences } from "../preferences/preferencesContext";
import { useToast } from "../components/toast/toastContext";
import { PermissionDeniedState } from "../components/states";
import { ToggleSwitch } from "../components/shared/ToggleSwitch";
import { projectionLookbackOptions, type PersonPreferences } from "../api/preferences";
import styles from "./PreferencesPage.module.css";
import { Dropdown } from "../components/shared/Dropdown";

/**
 * The Preferences screen, opened from the user menu: the signed-in person's own application preferences.
 * Each setting saves as soon as it is changed, and the outcome is reported with a toast. Every signed-in
 * person can change their own preferences; nobody can change another person's.
 */
export function PreferencesPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const { preferences, save } = usePreferences();
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const signedIn = snapshot.status === "signed-in";

  if (!signedIn) {
    return <PermissionDeniedState title={t("preferences.denied.title")} description={t("preferences.denied.description")} />;
  }

  const saveChange = async (next: PersonPreferences) => {
    setSaving(true);
    const result = await save(next);
    setSaving(false);
    if (result.kind === "ok") {
      toast.show(t("preferences.saved"), "success");
    } else {
      toast.show(t("preferences.saveFailed"), "error");
    }
  };

  return (
    <section className={styles.page} aria-labelledby="preferences-heading">
      <h1 id="preferences-heading">{t("preferences.title")}</h1>
      <p className={styles.note}>{t("preferences.explanation")}</p>
      <div className={styles.setting}>
        <div className={styles.text}>
          <h2 id="show-provider-warnings-label" className={styles.label}>
            {t("preferences.showProviderWarnings.label")}
          </h2>
          <p className={styles.note}>{t("preferences.showProviderWarnings.description")}</p>
        </div>
        <ToggleSwitch
          checked={preferences.showProviderWarnings}
          onChange={(checked) => void saveChange({ ...preferences, showProviderWarnings: checked })}
          labelledBy="show-provider-warnings-label"
          disabled={saving}
        />
      </div>
      <div className={styles.setting}>
        <div className={styles.text}>
          <h2 id="projection-lookback-label" className={styles.label}>
            {t("preferences.projectionLookback.label")}
          </h2>
          <p className={styles.note}>{t("preferences.projectionLookback.description")}</p>
        </div>
        <Dropdown
          id="projection-lookback"
          labelledBy="projection-lookback-label"
          options={projectionLookbackOptions.map((days) => ({ value: String(days), label: t("preferences.projectionLookback.days", { count: days }) }))}
          value={String(preferences.projectionLookbackDays)}
          disabled={saving}
          onChange={(value) => void saveChange({ ...preferences, projectionLookbackDays: Number(value) })}
        />
      </div>
      <div className={styles.setting}>
        <div className={styles.text}>
          <h2 id="animated-background-label" className={styles.label}>
            {t("preferences.animatedBackground.label")}
          </h2>
          <p className={styles.note}>{t("preferences.animatedBackground.description")}</p>
        </div>
        <ToggleSwitch
          checked={preferences.animatedBackground}
          onChange={(checked) => void saveChange({ ...preferences, animatedBackground: checked })}
          labelledBy="animated-background-label"
          disabled={saving}
        />
      </div>
    </section>
  );
}
